// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { getApiBaseUrl } from '../services/api';

interface UsageSummary {
  has_subscription: boolean;
  plan_name?: string;
  messages_used: number;
  messages_limit: number;
  messages_overage: number;
  usage_percentage: number;
  total_cost_cents: number;
  period_start?: string;
  period_end?: string;
}

const UsageIndicator: React.FC = () => {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsage();
    // Only fetch on mount, no polling
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/billing/usage/summary`);
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-400';
    if (percentage >= 75) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getProgressBarColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="px-4 py-3 bg-white/10 rounded-lg animate-pulse">
        <div className="h-4 bg-white/20 rounded w-3/4 mb-2"></div>
        <div className="h-2 bg-white/20 rounded"></div>
      </div>
    );
  }

  if (!usage || !usage.has_subscription) {
    return null; // Don't show if no subscription
  }

  const {
    messages_used = 0,
    messages_limit = 0,
    usage_percentage = 0,
    messages_overage = 0
  } = usage;

  const isOverage = messages_overage > 0;
  const displayPercentage = Math.min(usage_percentage || 0, 100);

  // Ultra-compact design for sidebar
  return (
    <div
      onClick={() => navigate('/client/billing?tab=usage')}
      className="relative px-3 py-2 bg-white/10 hover:bg-white/15 rounded-lg cursor-pointer transition-all duration-200 group"
      title={`${messages_used.toLocaleString()} / ${messages_limit.toLocaleString()} messages used (${(usage_percentage || 0).toFixed(0)}%)`}
    >
      {/* Compact single-line display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FontAwesomeIcon icon={faChartLine} className="text-blue-300 text-xs" />
          <span className="text-xs text-white font-medium">
            {messages_used.toLocaleString()}<span className="text-blue-200 mx-0.5">/</span>{messages_limit.toLocaleString()}
          </span>
        </div>

        {/* Status indicator */}
        <div className="flex items-center space-x-1.5">
          {isOverage && (
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-yellow-400 text-xs animate-pulse"
            />
          )}
          <span className={`text-xs font-semibold ${getUsageColor(usage_percentage || 0)}`}>
            {(usage_percentage || 0).toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Mini progress bar */}
      <div className="w-full bg-white/20 rounded-full h-1 mt-1.5 overflow-hidden">
        <div
          className={`h-full ${getProgressBarColor(usage_percentage)} transition-all duration-500`}
          style={{ width: `${displayPercentage}%` }}
        />
      </div>
    </div>
  );
};

export default UsageIndicator;
